import { recognize, type RecognizeResult } from "tesseract.js";
import { matchVendor } from "./vendorMatch";

export interface OcrResult {
  vendor: string;
  occurredOn: string;
  amountCents: number;
  taxAmountCents: number | null;
  currency: string;
  confidence: number;
  // Wave 14 — set when fuzzy vendor match clears the "did you mean?"
  // threshold (score 0.4–0.7) but wasn't confident enough to auto-swap
  // the OCR vendor string. Callers can render this as a soft suggestion.
  vendorSuggestion?: {
    canonicalName: string;
    score: number;
    source: "history" | "seeded";
  };
}

// Wave 14 — a single OCR-recognised word, with its bbox on the page. Keeping
// this flat (rather than Tesseract's nested block/paragraph/line structure)
// lets downstream helpers do cheap same-row lookups without re-walking the
// tree. Coordinates are in preprocessed-image pixel space.
export interface OcrWord {
  text: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  confidence: number;
}

const BYPASS = import.meta.env.VITE_E2E_BYPASS_AUTH === "true";

const FIXTURE: OcrResult = {
  vendor: "Stripe",
  occurredOn: "2026-03-15",
  amountCents: 12900,
  taxAmountCents: null,
  currency: "USD",
  confidence: 0.92,
};

async function renderPdfFirstPageToBlob(file: File): Promise<Blob> {
  // Dynamic import to keep PDF.js out of the initial bundle
  const pdfjs = await import("pdfjs-dist");
  // Set the worker source — use the bundled fake worker for simplicity in Vite
  // Point to the workerSrc from the pdfjs-dist package
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.mjs",
    import.meta.url,
  ).href;

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(1);

  const viewport = page.getViewport({ scale: 2.0 }); // 2x scale for better OCR
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas 2D context");

  await page.render({ canvasContext: ctx, viewport }).promise;

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Failed to convert PDF page to image blob"));
    }, "image/png");
  });
}

export async function extractReceipt(file: File): Promise<OcrResult> {
  if (BYPASS) {
    await new Promise((r) => setTimeout(r, 150));
    return FIXTURE;
  }

  let imageBlob: Blob;
  if (file.type === "application/pdf") {
    imageBlob = await renderPdfFirstPageToBlob(file);
  } else if (file.type.startsWith("image/")) {
    imageBlob = await preprocessImage(file);
  } else {
    throw new Error("Unsupported file type for OCR. Use PNG, JPG, or PDF.");
  }

  const result = await recognize(imageBlob, "eng");
  return parseReceipt(result);
}

// ---- preprocessing ----

/**
 * Best-effort image preprocessing: grayscale + fixed threshold + optional 2x
 * upscale. Tesseract tends to do noticeably better on high-contrast, large
 * glyph images. Any failure falls back to the original file so OCR still
 * runs on something. Never throws.
 */
export async function preprocessImage(file: File): Promise<Blob> {
  // Guard: non-browser environments or missing APIs → return original.
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    typeof URL === "undefined" ||
    typeof URL.createObjectURL !== "function"
  ) {
    return file;
  }

  let objectUrl: string | null = null;
  try {
    objectUrl = URL.createObjectURL(file);
    const img = await loadImage(objectUrl);

    const srcW = img.naturalWidth || img.width;
    const srcH = img.naturalHeight || img.height;
    if (!srcW || !srcH) return file;

    // Upscale 2x if the image is small — Tesseract struggles with tiny glyphs.
    const scale = srcW < 1200 ? 2 : 1;
    const outW = Math.round(srcW * scale);
    const outH = Math.round(srcH * scale);

    const canvas = createCanvas(outW, outH);
    const ctx = getContext2D(canvas);
    if (!ctx) return file;

    // Upscale with smoothing first for better antialiasing, then threshold.
    // `imageSmoothingQuality` is a hint — not every UA honors it but it's
    // harmless when unsupported.
    const anyCtx = ctx as unknown as {
      imageSmoothingEnabled?: boolean;
      imageSmoothingQuality?: "low" | "medium" | "high";
    };
    anyCtx.imageSmoothingEnabled = true;
    anyCtx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, outW, outH);

    // Grayscale + fixed threshold at 128. Otsu would be better but adds code
    // for marginal gain on thermal receipts where contrast is already poor.
    const imageData = ctx.getImageData(0, 0, outW, outH);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
      const v = avg < 128 ? 0 : 255;
      data[i] = v;
      data[i + 1] = v;
      data[i + 2] = v;
      // alpha untouched
    }
    ctx.putImageData(imageData, 0, 0);

    const blob = await canvasToBlob(canvas);
    return blob ?? file;
  } catch {
    return file;
  } finally {
    if (objectUrl) {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {
        /* ignore */
      }
    }
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image-load-failed"));
    img.src = src;
  });
}

type AnyCanvas = HTMLCanvasElement | OffscreenCanvas;

function createCanvas(w: number, h: number): AnyCanvas {
  if (typeof OffscreenCanvas !== "undefined") {
    try {
      return new OffscreenCanvas(w, h);
    } catch {
      /* fall through */
    }
  }
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

function getContext2D(
  canvas: AnyCanvas,
): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null {
  // Cast via unknown: the 2 canvas flavours have structurally-identical
  // `getContext('2d')` signatures but TypeScript's overload resolution
  // doesn't unify them when `canvas` is a union.
  return (
    (canvas as unknown as {
      getContext: (
        id: "2d",
      ) => CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
    }).getContext("2d") ?? null
  );
}

async function canvasToBlob(canvas: AnyCanvas): Promise<Blob | null> {
  // OffscreenCanvas exposes `convertToBlob`; HTMLCanvasElement exposes
  // `toBlob(cb)`. Handle both.
  const off = canvas as Partial<OffscreenCanvas>;
  if (typeof off.convertToBlob === "function") {
    try {
      return await off.convertToBlob.call(canvas, { type: "image/png" });
    } catch {
      return null;
    }
  }
  const html = canvas as HTMLCanvasElement;
  if (typeof html.toBlob === "function") {
    return new Promise<Blob | null>((resolve) => {
      html.toBlob((b) => resolve(b), "image/png");
    });
  }
  return null;
}

// ---- parser ----

/**
 * Flatten Tesseract's nested block/paragraph/line/word tree into a flat
 * `OcrWord[]` with absolute page coordinates. Words with confidence below 30
 * (Tesseract's 0-100 scale) are dropped — they're almost always noise from
 * stray pixels and tend to pollute same-row lookups.
 *
 * Tesseract's runtime sometimes exposes `page.words` directly, but its type
 * definitions only promise the nested form. Walk defensively; treat missing
 * levels as empty arrays.
 */
export function extractWords(result: RecognizeResult): OcrWord[] {
  const words: OcrWord[] = [];
  const page = result.data as unknown as {
    blocks?: Array<{
      paragraphs?: Array<{
        lines?: Array<{
          words?: Array<{
            text?: string;
            confidence?: number;
            bbox?: { x0: number; y0: number; x1: number; y1: number };
          }>;
        }>;
      }>;
    }>;
    words?: Array<{
      text?: string;
      confidence?: number;
      bbox?: { x0: number; y0: number; x1: number; y1: number };
    }>;
  };

  // Prefer the flat `page.words` when present (Tesseract.js v4+ sometimes
  // surfaces it at runtime) — it saves a tree walk.
  const flatWords = page.words;
  if (Array.isArray(flatWords) && flatWords.length > 0) {
    for (const w of flatWords) {
      const mapped = mapWord(w);
      if (mapped) words.push(mapped);
    }
    return words;
  }

  const blocks = page.blocks ?? [];
  for (const block of blocks) {
    for (const para of block.paragraphs ?? []) {
      for (const line of para.lines ?? []) {
        for (const w of line.words ?? []) {
          const mapped = mapWord(w);
          if (mapped) words.push(mapped);
        }
      }
    }
  }
  return words;
}

function mapWord(w: {
  text?: string;
  confidence?: number;
  bbox?: { x0: number; y0: number; x1: number; y1: number };
}): OcrWord | null {
  const text = (w.text ?? "").trim();
  if (!text) return null;
  const confidence = typeof w.confidence === "number" ? w.confidence : 0;
  if (confidence < 30) return null; // noise filter
  const bbox = w.bbox;
  if (!bbox) return null;
  return {
    text,
    x0: bbox.x0,
    y0: bbox.y0,
    x1: bbox.x1,
    y1: bbox.y1,
    confidence,
  };
}

export function parseReceipt(result: RecognizeResult): OcrResult {
  const text = result.data.text;
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  // Word-level recognition is the backbone of the Wave 14 layout-aware
  // helpers. Collected here so we only walk the tree once per receipt.
  const words = extractWords(result);

  // Layout-aware total extraction fires first — it recovers column-aligned
  // receipts where the line-based regex parser sees `Total` and the amount
  // on different "lines" because Tesseract's line segmentation split the
  // columns. Fall back to the scored line parser when layout yields nothing.
  const amountCents =
    extractTotalCentsFromLayout(words) ?? extractTotalCents(lines) ?? 0;

  // Fuzzy vendor match: score the top-of-receipt lines against the org's
  // vendor history plus the seeded dictionary. If we're confident (>0.7),
  // swap in the canonical name — the known-vendor spelling is almost
  // always cleaner than whatever OCR returned. If we're only moderately
  // confident (0.4–0.7), keep the OCR line but surface the match as a
  // "did you mean?" suggestion.
  const rawVendor = extractVendor(lines);
  const match = matchVendor(lines);
  let vendor = rawVendor;
  let vendorSuggestion: OcrResult["vendorSuggestion"];
  if (match) {
    if (match.score > 0.7) {
      vendor = match.canonicalName;
    } else if (match.score >= 0.4) {
      vendorSuggestion = {
        canonicalName: match.canonicalName,
        score: match.score,
        source: match.source,
      };
    }
  }

  return {
    vendor,
    occurredOn: extractDate(lines) ?? todayIso(),
    amountCents,
    taxAmountCents: extractTaxCents(lines),
    currency: extractCurrency(text),
    confidence: result.data.confidence / 100, // Tesseract reports 0-100
    ...(vendorSuggestion ? { vendorSuggestion } : {}),
  };
}

// Labels that, on their own, mean "the number to the right of this word is
// the payable amount". We deliberately exclude `subtotal`, `paid`, `change`,
// `tip` — those are different columns that happen to share the row layout.
const TOTAL_LABEL_RX = /^(grand\s+)?total$|^amount$|^balance$/i;
// Anything that looks like money to the right of a label. Optional $ and
// comma thousands separators; require a 2-digit cents tail to avoid eating
// phone fragments or quantities.
const MONEY_TOKEN_RX = /^\$?([0-9][0-9,]*\.\d{2})$/;

/**
 * Column-aware total extraction. Finds label tokens (`Total`, `Amount`,
 * `Balance`, `Grand Total`) and looks rightward on the same Y-band for a
 * money token. Returns the rightmost money token on the last matching row
 * (receipts typically list subtotal first, grand total last).
 *
 * Returns null if no label/money pair is found — caller falls back to the
 * line-based parser.
 */
export function extractTotalCentsFromLayout(
  words: OcrWord[],
): number | null {
  if (words.length === 0) return null;

  // Match label tokens. `Grand Total` spans two words, so we also look at
  // the concatenation of consecutive words starting each position.
  const labelRows: number[] = []; // center-y of each matching row
  for (let i = 0; i < words.length; i++) {
    const w = words[i];
    const single = w.text.replace(/[.,:;]+$/, "");
    const twoWord =
      i + 1 < words.length
        ? `${single} ${words[i + 1].text.replace(/[.,:;]+$/, "")}`
        : "";
    if (
      TOTAL_LABEL_RX.test(single) ||
      /^grand\s+total$/i.test(twoWord)
    ) {
      labelRows.push((w.y0 + w.y1) / 2);
    }
  }

  if (labelRows.length === 0) return null;

  // For each label row (in order), collect same-row money tokens to the
  // right of that label. Keep the last-row match — receipts usually list
  // subtotal first and grand total last.
  let chosen: number | null = null;
  for (const centerY of labelRows) {
    // Find label x-extent on this row (leftmost label word on the row).
    let labelRight = -Infinity;
    for (const w of words) {
      const wy = (w.y0 + w.y1) / 2;
      if (Math.abs(wy - centerY) >= 10) continue;
      const t = w.text.replace(/[.,:;]+$/, "");
      if (TOTAL_LABEL_RX.test(t)) {
        labelRight = Math.max(labelRight, w.x1);
      }
    }
    if (!Number.isFinite(labelRight)) continue;

    // Collect money tokens to the right of the label on the same row.
    const rowMoney: Array<{ cents: number; x0: number }> = [];
    for (const w of words) {
      const wy = (w.y0 + w.y1) / 2;
      if (Math.abs(wy - centerY) >= 10) continue;
      if (w.x0 < labelRight) continue;
      const m = w.text.match(MONEY_TOKEN_RX);
      if (!m) continue;
      const cents = parseMoneyToCents(m[1]);
      if (cents === null || cents <= 0) continue;
      rowMoney.push({ cents, x0: w.x0 });
    }
    if (rowMoney.length === 0) continue;

    // Rightmost money token on the row wins — in a two-column layout the
    // label sits on the left and the payable amount on the far right.
    rowMoney.sort((a, b) => b.x0 - a.x0);
    chosen = rowMoney[0].cents;
    // Don't break: let the LAST matching label row overwrite earlier picks
    // so subtotal rows lose to the grand-total row.
  }

  return chosen;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Vendor: score the top 6 lines. We prefer short-to-medium uppercase,
// mostly-alphabetic lines. Noise (URLs, phones, addresses, pure numbers,
// "Tel"/"Fax" fields) is penalised heavily.
//
// Scoring rationale is documented inline next to each rule so the heuristic
// stays auditable when receipts in the wild break it.
const NOISE_RX =
  /(https?:\/\/|www\.|^\d+[-.\s()]*\d+[-.\s()]*\d+$|^\$|^\d{5}(-\d{4})?$|\b(street|st|ave|avenue|blvd|road|rd|suite|ste)\b|^\d+\s+\w+\s+(street|st|ave|avenue|blvd|road|rd))/i;

function alphaRatio(s: string): number {
  if (!s.length) return 0;
  const alpha = (s.match(/[A-Za-z]/g) ?? []).length;
  return alpha / s.length;
}

function upperRatio(s: string): number {
  const alpha = s.match(/[A-Za-z]/g) ?? [];
  if (alpha.length === 0) return 0;
  const upper = alpha.filter((c) => c >= "A" && c <= "Z").length;
  return upper / alpha.length;
}

function isNoise(line: string): boolean {
  if (NOISE_RX.test(line)) return true;
  // Pure-number lines (totals, barcodes, etc.) have no alphabetic content.
  if (!/[A-Za-z]/.test(line)) return true;
  return false;
}

function scoreVendorLine(line: string): number {
  let score = 0;
  if (upperRatio(line) >= 0.6) score += 3;
  if (line.length >= 4 && line.length <= 40) score += 2;
  if (alphaRatio(line) >= 0.5) score += 1;
  if (isNoise(line)) score -= 5;
  if (/^#|^tel\b|^fax\b/i.test(line)) score -= 5;
  return score;
}

function cleanVendor(line: string): string {
  return line
    .replace(/^[\s*\-]+/, "")
    .replace(/[\s*\-.,;:]+$/, "")
    .trim();
}

function extractVendor(lines: string[]): string {
  const top = lines.slice(0, 6);
  if (top.length === 0) return "Unknown";

  let best: { line: string; score: number } | null = null;
  for (const line of top) {
    const s = scoreVendorLine(line);
    if (!best || s > best.score) best = { line, score: s };
  }

  if (best && best.score >= 3) return cleanVendor(best.line) || "Unknown";

  // Fallback: longest line in the top 3 with enough alphabetic content.
  const candidates = lines
    .slice(0, 3)
    .filter((l) => alphaRatio(l) >= 0.4)
    .sort((a, b) => b.length - a.length);
  if (candidates.length > 0) return cleanVendor(candidates[0]) || "Unknown";

  return "Unknown";
}

// Date: collect ALL matches across supported formats, prefer lines that
// aren't tagged "ready by", "pickup", "due", etc. — those are usually
// future dates, not the transaction date. Among eligible matches, return
// the earliest; transactions precede pickups.
const MONTHS_MAP: Record<string, string> = {
  jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06",
  jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12",
};
const FUTURE_LINE_RX = /\b(ready|pickup|pick\s*up|due|expire|expires|return|by)\b/i;

function toTwoDigitYearIso(mm: string, dd: string, yy: string): string {
  const y = `20${yy.padStart(2, "0")}`;
  return `${y}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
}

interface DateHit {
  iso: string;
  lineIndex: number;
}

function collectDatesOnLine(line: string, lineIndex: number): DateHit[] {
  const hits: DateHit[] = [];

  // yyyy-mm-dd or yyyy/mm/dd
  for (const m of line.matchAll(
    /\b(20\d{2})[-/](0?[1-9]|1[0-2])[-/](0?[1-9]|[12]\d|3[01])\b/g,
  )) {
    hits.push({
      iso: `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`,
      lineIndex,
    });
  }

  // mm/dd/yyyy (four-digit year)
  for (const m of line.matchAll(
    /\b(0?[1-9]|1[0-2])[/.-](0?[1-9]|[12]\d|3[01])[/.-](20\d{2})\b/g,
  )) {
    hits.push({
      iso: `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`,
      lineIndex,
    });
  }

  // mm/dd/yy (two-digit year) — assume 20YY. Use lookaround-lite by
  // ensuring we don't glue onto a longer digit run on either side.
  for (const m of line.matchAll(
    /(?<!\d)(0?[1-9]|1[0-2])[/.-](0?[1-9]|[12]\d|3[01])[/.-](\d{2})(?!\d)/g,
  )) {
    hits.push({
      iso: toTwoDigitYearIso(m[1], m[2], m[3]),
      lineIndex,
    });
  }

  // DD Mon YYYY
  for (const m of line.matchAll(
    /\b(0?[1-9]|[12]\d|3[01])\s+([A-Za-z]{3,9})\s+(20\d{2})\b/g,
  )) {
    const mon = MONTHS_MAP[m[2].slice(0, 3).toLowerCase()];
    if (mon) {
      hits.push({
        iso: `${m[3]}-${mon}-${m[1].padStart(2, "0")}`,
        lineIndex,
      });
    }
  }

  // Mon DD, YYYY
  for (const m of line.matchAll(
    /\b([A-Za-z]{3,9})\s+(0?[1-9]|[12]\d|3[01])(?:,)?\s+(20\d{2})\b/g,
  )) {
    const mon = MONTHS_MAP[m[1].slice(0, 3).toLowerCase()];
    if (mon) {
      hits.push({
        iso: `${m[3]}-${mon}-${m[2].padStart(2, "0")}`,
        lineIndex,
      });
    }
  }

  return hits;
}

export interface DateCandidate {
  iso: string;
  lineIdx: number;
  context: string;
}

/**
 * Collect every date regex match on every line, annotated with its line
 * index + the full line as context. Exposed for tests + downstream tools.
 */
export function collectDateCandidates(lines: string[]): DateCandidate[] {
  const out: DateCandidate[] = [];
  lines.forEach((line, idx) => {
    for (const hit of collectDatesOnLine(line, idx)) {
      out.push({ iso: hit.iso, lineIdx: idx, context: line });
    }
  });
  return out;
}

/**
 * Check whether an ISO date sits inside the "plausible transaction date"
 * window: today minus 2 years through today plus 30 days. Anything outside
 * this range is almost certainly a typo / OCR misread of digits on a
 * barcode. Isolated as a helper so test doubles can patch the clock.
 */
export function isPlausibleReceiptDate(
  iso: string,
  now: Date = new Date(),
): boolean {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  const nowMs = now.getTime();
  const msPerDay = 24 * 3600 * 1000;
  const lower = nowMs - 2 * 365 * msPerDay; // rough — leap-year slack OK
  const upper = nowMs + 30 * msPerDay;
  return t >= lower && t <= upper;
}

function extractDate(lines: string[]): string | null {
  const candidates = collectDateCandidates(lines);
  if (candidates.length === 0) return null;

  const now = new Date();
  // Score each candidate. Tiebreak by earliest line index (transaction
  // dates usually appear before "ready by" lines on receipts).
  interface Scored {
    iso: string;
    lineIdx: number;
    score: number;
  }
  const scored: Scored[] = candidates.map((c) => {
    let score = 0;
    if (!FUTURE_LINE_RX.test(c.context)) score += 3;
    if (isPlausibleReceiptDate(c.iso, now)) {
      score += 2;
    } else {
      score -= 5;
    }
    return { iso: c.iso, lineIdx: c.lineIdx, score };
  });

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    // Lower lineIdx wins among ties (earlier on the receipt).
    if (a.lineIdx !== b.lineIdx) return a.lineIdx - b.lineIdx;
    // Final tiebreak: earliest ISO. Keeps behaviour stable when two dates
    // share a line (e.g. "Ordered 03/10, Shipped 03/12").
    return a.iso.localeCompare(b.iso);
  });

  return scored[0].iso;
}

// Total: collect every $-amount on the receipt, score each by its line
// context, and pick the highest-scored non-zero amount. This beats the
// previous "first total line wins" approach on receipts that have
// paid/balance/change columns where `Total` and the number don't sit
// side-by-side (e.g., laundromat receipts with columnar "Paid / Balance").
const MONEY_G_RX = /\$?\s*([0-9]+(?:[.,][0-9]{2}))/g;

interface AmountCandidate {
  cents: number;
  line: string;
  lineIndex: number;
  score: number;
}

function parseMoneyToCents(raw: string): number | null {
  const cents = Math.round(parseFloat(raw.replace(",", ".")) * 100);
  return Number.isFinite(cents) ? cents : null;
}

function extractTotalCents(lines: string[]): number | null {
  const candidates: AmountCandidate[] = [];

  lines.forEach((line, idx) => {
    for (const m of line.matchAll(MONEY_G_RX)) {
      const cents = parseMoneyToCents(m[1]);
      if (cents === null) continue;
      candidates.push({ cents, line, lineIndex: idx, score: 0 });
    }
  });

  if (candidates.length === 0) return null;

  const nonZero = candidates.filter((c) => c.cents > 0);
  const maxNonZero = nonZero.reduce((a, c) => Math.max(a, c.cents), 0);

  for (const c of candidates) {
    const lower = c.line.toLowerCase();
    const neighbour = c.lineIndex > 0 ? lines[c.lineIndex - 1].toLowerCase() : "";

    // Strong total signals.
    if (/grand\s+total|amount\s+due|balance\s+due|total\s+due/.test(lower)) {
      c.score += 5;
    }
    // "Total" or "balance" on its own line — also covers the column-split
    // case where a label on line N names the amount on line N (or N-1 gave
    // the label and this line has only a number).
    if (/\btotal\b/.test(lower) || /\bbalance\b/.test(lower)) {
      c.score += 3;
    }
    // Column-split look-back (one line): label on prior line, number here.
    if (
      !/\b(total|balance|amount\s+due)\b/.test(lower) &&
      /\b(grand\s+total|amount\s+due|balance\s+due|total\s+due|total)\b/.test(
        neighbour,
      )
    ) {
      c.score += 3;
    }

    // Largest-nonzero-amount bonus — the grand total is usually the
    // biggest number on a receipt, so lean into that when labels fail.
    if (c.cents > 0 && c.cents === maxNonZero) c.score += 2;

    // Penalties.
    if (/sub[-\s]*total/.test(lower)) c.score -= 10;
    if (/\b(paid|change|tip|tender)\b/.test(lower)) c.score -= 5;
    // Explicit zero-amount penalty — rows like "Tax: 0.00" or "Tip: 0.00"
    // consistently lose to any non-zero candidate.
    if (c.cents === 0) c.score -= 5;
  }

  // Pick highest-scored candidate; break score ties by larger cents
  // value (the grand total is almost always the largest number sharing
  // its label with anything else).
  const ranked = [...candidates].sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return b.cents - a.cents;
  });
  const topNonZero = ranked.find((c) => c.cents > 0 && c.score > 0);
  if (topNonZero) return topNonZero.cents;

  // Fallback: largest non-zero amount on the receipt.
  if (maxNonZero > 0) return maxNonZero;

  return ranked[0]?.cents ?? null;
}

function extractTaxCents(lines: string[]): number | null {
  const TAX_RX = /\b(tax|vat|gst|hst|pst)\b/i;
  const MONEY_RX = /\$?\s*([0-9]+(?:[.,][0-9]{2}))/g;
  for (const line of lines) {
    if (!TAX_RX.test(line)) continue;
    if (/total/i.test(line)) continue;
    const matches = Array.from(line.matchAll(MONEY_RX));
    if (matches.length === 0) continue;
    const last = matches[matches.length - 1][1];
    const cents = Math.round(parseFloat(last.replace(",", ".")) * 100);
    if (Number.isFinite(cents)) return cents;
  }
  return null;
}

function extractCurrency(text: string): string {
  if (/€|\bEUR\b/.test(text)) return "EUR";
  if (/£|\bGBP\b/.test(text)) return "GBP";
  if (/¥|\bJPY\b/.test(text)) return "JPY";
  return "USD";
}
